import type { ColumnFilterEntry } from "./claims";

export type TicketColumnFilters = Record<string, ColumnFilterEntry | string[]>;

export interface IAttachment {
  id: number;
  filename: string;
  size: string;
  content_type: string;
}

export interface IArticle {
  id: number;
  ticket_id: number;
  from: string;
  to: string;
  subject: string;
  body: string;
  content_type: string;
  internal: boolean;
  created_at: string;
  updated_at: string;
  sender: string;
  type: string;
  attachments: IAttachment[];
}

export interface ITicket {
  id: number;
  number: string;
  title: string;
  state: string;
  state_id: number;
  priority: string;
  priority_id: number;
  group: string;
  owner: string;
  customer: string;
  created_at: string;
  updated_at: string;
  /** Returned by the list, detail and support-upload endpoints; older rows may lack it. */
  reply_count?: number;
  /** Not yet returned by the list endpoint; falls back to false until the backend adds it. */
  has_unread_reply?: boolean;
}

/** One entry in the ticket's flat `files` collection (REV-2511). */
export interface ITicketFile {
  id: string;
  article_id: number;
  attachment_id: number | null;
  filename: string;
  size: number | null;
  mime_type: string | null;
  created_at: string;
  /** "attachment": Zammad holds the bytes. "link": the support form stored it in S3. */
  source: "attachment" | "link";
  s3_key?: string;
  /** Root-relative backend path, e.g. /api/v1/files/stream/?s3_key=... */
  download_url: string;
}

export interface ITicketDetail extends ITicket {
  articles: IArticle[];
  files?: ITicketFile[];
}

export interface TicketChangedFrame {
  type: "ticket_changed";
  ticket_id: number;
  number: string;
  updated_at: string;
}

export interface ITicketListResponse {
  tickets: ITicket[];
  tickets_count: number;
  assets: Record<string, unknown>;
}

/** Column ids and labels for the tickets grid (visibility + pin toggles). */
export const TICKETS_TABLE_COLUMN_OPTIONS = [
  { id: "number", label: "Ticket #", required: true, mandatory: true },
  { id: "title", label: "Subject" },
  { id: "status", label: "Status" },
  { id: "priority", label: "Priority" },
  { id: "created_at", label: "Created" },
  { id: "updated_at", label: "Last update" },
] as Array<{
  id: string;
  label: string;
  required?: boolean;
  visible?: boolean;
  mandatory?: boolean;
}>;

/** Column id derived from TICKETS_TABLE_COLUMN_OPTIONS. */
export type TicketsTableColumnId = (typeof TICKETS_TABLE_COLUMN_OPTIONS)[number]["id"];

/** Per-column user preference: visibility and pin (required) state. */
export interface TicketsTableColumnSetting {
  id: TicketsTableColumnId;
  visible: boolean;
  required: boolean;
}
